import React, { useEffect, useState, useCallback } from 'react';
import { showToast } from '../common/Toaster';
import ConfirmDialog from '../common/ConfirmDialog';
import { Plus, Trash2, CalendarCheck, Check, X, Clock, MessageSquare, CalendarPlus, Video, Users, Pencil } from 'lucide-react';
import planApi, { PlanRole } from '../../services/planApi';

interface Props {
  relId: number;
  role: PlanRole;
  consentOk: boolean;
  onChanged: () => void;
}

const toSql = (v: string) => (v ? v.replace('T', ' ') + ':00' : '');
const toLocalInput = (sql: string) => (sql ? String(sql).replace(' ', 'T').slice(0, 16) : '');
const MESES = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
const RATING_LBL = ['', 'Muy mala', 'Regular', 'Aceptable', 'Buena', 'Excelente'];
const CATS: Record<string, { label: string; pct: string }> = {
  '70': { label: 'Experiencia', pct: '70' }, '20': { label: 'De otros', pct: '20' }, '10': { label: 'Formal', pct: '10' },
};
const TITLE_TO_FOCUS: Record<string, 'foda' | 'pdi' | 'avances'> = {
  'Revisión del FODA personal': 'foda',
  'Construcción del PDI': 'pdi',
  'Revisión de avances': 'avances',
};
const fmt = (sql: string) => {
  const d = new Date(String(sql).replace(' ', 'T'));
  return isNaN(d.getTime()) ? sql : d.toLocaleString('es', { dateStyle: 'medium', timeStyle: 'short' });
};

const STATUS: Record<string, { label: string; cls: string }> = {
  unscheduled: { label: 'Por agendar', cls: 'st-unscheduled' },
  pending:     { label: 'Pendiente de aprobación', cls: 'st-pending' },
  scheduled:   { label: 'Agendada', cls: 'st-scheduled' },
  completed:   { label: 'Efectiva', cls: 'st-completed' },
  cancelled:   { label: 'Cancelada', cls: 'st-cancelled' },
  no_show:     { label: 'No asistió', cls: 'st-cancelled' },
};

const Availability: React.FC<Props> = ({ relId, role, onChanged }) => {
  const isMentee = role === 'mentee';
  const [slots, setSlots] = useState<any[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);
  const [comps, setComps] = useState<any[]>([]);
  const [newSlot, setNewSlot] = useState('');
  const [busy, setBusy] = useState(false);

  const [total, setTotal] = useState(12);

  // Agendar una NUEVA sesión (mentee) — las sesiones se crean al agendar
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [editingSession, setEditingSession] = useState<any | null>(null);
  const [pickedSlot, setPickedSlot] = useState<number | null>(null);
  const [freeDate, setFreeDate] = useState('');
  const [objectives, setObjectives] = useState<number[]>([]);
  const [focus, setFocus] = useState<'foda' | 'pdi' | 'avances'>('avances');
  const [modality, setModality] = useState<'teams' | 'presencial'>('teams');
  const [duration, setDuration] = useState(60);

  // Feedback / ¿se realizó?
  const [confirmNo, setConfirmNo] = useState<any | null>(null); // sesión a marcar "no realizada"
  const [confirmCancel, setConfirmCancel] = useState<any | null>(null); // sesión a cancelar
  const [confirmDelete, setConfirmDelete] = useState<any | null>(null); // sesión cancelada a eliminar

  // Registrar cómo fue la sesión
  const [regSession, setRegSession] = useState<any | null>(null);
  const [regRating, setRegRating] = useState(0);
  const [regCompleted, setRegCompleted] = useState<boolean | null>(null);
  const [regFeedback, setRegFeedback] = useState('');
  const [regAdminNote, setRegAdminNote] = useState('');
  const [regActions, setRegActions] = useState<Set<number>>(new Set());
  const [fbSession, setFbSession] = useState<any | null>(null); // sesión para capturar feedback
  const [fbMode, setFbMode] = useState<'feedback' | 'outcome'>('feedback');
  const [fbText, setFbText] = useState('');
  const [savingFb, setSavingFb] = useState(false);

  const load = useCallback(async () => {
    const [a, s] = await Promise.all([planApi.getAvailability(relId), planApi.getSessions(relId)]);
    setSlots(a.slots || []);
    // Orden: próximas primero (la más cercana arriba), pasadas al final (la más reciente primero).
    const now = Date.now();
    const ts = (v: any) => { const d = v ? new Date(String(v).replace(' ', 'T')).getTime() : NaN; return isNaN(d) ? 0 : d; };
    const ordered = (s.sessions || []).slice().sort((a: any, b: any) => {
      const da = ts(a.scheduled_date), db = ts(b.scheduled_date);
      const aPast = da < now, bPast = db < now;
      if (aPast !== bPast) return aPast ? 1 : -1;   // próximas arriba, pasadas abajo
      return aPast ? db - da : da - db;              // pasadas: más reciente primero; próximas: más cercana primero
    });
    setSessions(ordered);
    setTotal(s.total || 12);
    try { const p = await planApi.getPdi(relId); setComps(p.competencies || []); } catch { /* PDI bloqueado */ }
  }, [relId]);
  useEffect(() => { load(); }, [load]);

  const available = slots.filter(s => s.status === 'available');
  const toggleObjective = (id: number) => setObjectives(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const openNewSession = () => {
    setEditingSession(null); setScheduleOpen(true); setPickedSlot(null); setFreeDate(''); setObjectives([]);
    setFocus('avances'); setModality('teams'); setDuration(60);
  };
  const openEditSession = (s: any) => {
    setEditingSession(s); setScheduleOpen(true); setPickedSlot(null);
    setFocus(TITLE_TO_FOCUS[s.title] || 'avances');
    setObjectives(s.objective_ids || []);
    setFreeDate(toLocalInput(s.scheduled_date));
    setDuration(s.duration_minutes || 60);
    setModality(s.session_type === 'in_person' ? 'presencial' : 'teams');
  };
  const closeSchedule = () => { setScheduleOpen(false); setEditingSession(null); };
  const confirmSchedule = async () => {
    setBusy(true);
    try {
      if (!freeDate) { showToast('Elige la fecha y hora de la sesión', 'error'); return; }
      if (editingSession) {
        await planApi.updateSession(editingSession.id, { focus, scheduled_date: toSql(freeDate), duration_minutes: duration, modality, pdi_competency_ids: objectives });
      } else {
        await planApi.bookFree(relId, { focus, scheduled_date: toSql(freeDate), duration_minutes: duration, modality, pdi_competency_ids: objectives });
      }
      closeSchedule(); await load(); onChanged();
    } catch (e: any) { showToast(e?.response?.data?.message || 'No se pudo guardar', 'error'); }
    finally { setBusy(false); }
  };

  const addSlot = async () => {
    if (!newSlot) return;
    setBusy(true);
    try { await planApi.publishAvailability(relId, [{ slot_datetime: toSql(newSlot) }]); setNewSlot(''); await load(); onChanged(); }
    finally { setBusy(false); }
  };
  const delSlot = async (id: number) => { await planApi.deleteSlot(id); await load(); };
  const accept = async (id: number) => { await planApi.acceptSession(id); await load(); onChanged(); };
  const reject = async (id: number) => { await planApi.rejectSession(id); await load(); onChanged(); };

  const outcomeNo = (s: any) => setConfirmNo(s);
  const doOutcomeNo = async () => {
    const s = confirmNo; setConfirmNo(null);
    if (!s) return;
    await planApi.markOutcome(s.id, { happened: false }); await load(); onChanged();
  };
  const doCancel = async () => {
    const s = confirmCancel; setConfirmCancel(null);
    if (!s) return;
    try { await planApi.cancelSession(s.id); await load(); onChanged(); }
    catch (e: any) { showToast(e?.response?.data?.message || 'No se pudo cancelar', 'error'); }
  };
  const actionsForSession = (s: any) => {
    const ids: number[] = (s?.objective_ids || []).map(Number);
    return comps.filter((c: any) => ids.includes(Number(c.id))).flatMap((c: any) => (c.actions || []).map((a: any) => ({ ...a, comp: c.title })));
  };
  const openRegister = (s: any) => {
    setRegSession(s); setRegRating(0); setRegCompleted(null); setRegFeedback(''); setRegAdminNote(''); setRegActions(new Set());
  };
  const closeRegister = () => setRegSession(null);
  const toggleRegAction = (id: number) => setRegActions(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const submitRegister = async () => {
    if (!regSession) return;
    // El mentee marca objetivo y avances; el mentor solo califica y deja mensajes.
    if (isMentee) {
      if (regCompleted === null) { showToast('Indica si se cumplió el objetivo de la sesión', 'error'); return; }
      const hasActions = actionsForSession(regSession).some((a: any) => Number(a.completed) !== 1);
      if (hasActions && regActions.size === 0) { showToast('Marca al menos un avance (acción trabajada)', 'error'); return; }
    }
    if (!regRating) { showToast('Califica la sesión con estrellas', 'error'); return; }
    setBusy(true);
    try {
      await planApi.registerSession(regSession.id, {
        rating: regRating, feedback: regFeedback.trim(), admin_note: regAdminNote.trim(),
        ...(isMentee ? { completed_objective: regCompleted as boolean, action_ids: Array.from(regActions) } : {}),
      });
      setRegSession(null); await load(); onChanged();
    } catch (e: any) { showToast(e?.response?.data?.message || 'No se pudo registrar', 'error'); }
    finally { setBusy(false); }
  };
  const doDeleteSession = async () => {
    const s = confirmDelete; setConfirmDelete(null);
    if (!s) return;
    try { await planApi.deleteSession(s.id); await load(); onChanged(); }
    catch (e: any) { showToast(e?.response?.data?.message || 'No se pudo eliminar', 'error'); }
  };
  const openOutcomeYes = (s: any) => { setFbSession(s); setFbMode('outcome'); setFbText(''); };
  const openFeedback = (s: any) => { setFbSession(s); setFbMode('feedback'); setFbText(s.mentee_feedback || ''); };
  const submitFb = async () => {
    if (!fbSession) return;
    setSavingFb(true);
    try {
      if (fbMode === 'outcome') {
        await planApi.markOutcome(fbSession.id, { happened: true, feedback: fbText.trim() });
      } else {
        await planApi.markOutcome(fbSession.id, { happened: true, feedback: fbText.trim() }); // efectiva + feedback
      }
      setFbSession(null); await load(); onChanged();
    } catch (e: any) { showToast(e?.response?.data?.message || 'No se pudo guardar', 'error'); }
    finally { setSavingFb(false); }
  };

  const objectivePicker = comps.length > 0 && (
    <div className="obj-picker">
      <span className="obj-picker-label">Objetivos del PDI (opcional) — puedes elegir varios:</span>
      <div className="obj-chips">
        {comps.map(c => {
          const on = objectives.includes(Number(c.id));
          return (
            <button type="button" key={c.id} className={`obj-chip ${on ? 'on' : ''}`} onClick={() => toggleObjective(Number(c.id))}>
              {on && <Check size={12} strokeWidth={3} />} {c.title}
            </button>
          );
        })}
      </div>
    </div>
  );

  const effective = sessions.filter(s => s.status === 'completed').length;
  const objsOf = (s: any): string[] => (s.objectives && s.objectives.length) ? s.objectives : (s.objective ? [s.objective] : []);

  return (
    <>
    <ConfirmDialog
      open={!!confirmNo}
      danger
      title="¿La sesión no se realizó?"
      message={<>La sesión se cancelará y se liberará el horario. Podrás agendar otra cuando quieras.</>}
      confirmLabel="Sí, no se realizó"
      cancelLabel="Volver"
      onConfirm={doOutcomeNo}
      onCancel={() => setConfirmNo(null)}
    />
    <ConfirmDialog
      open={!!confirmDelete}
      danger
      title="Eliminar esta sesión"
      message={<>Se eliminará definitivamente esta sesión cancelada. Esta acción no se puede deshacer.</>}
      confirmLabel="Eliminar"
      cancelLabel="Volver"
      onConfirm={doDeleteSession}
      onCancel={() => setConfirmDelete(null)}
    />
    <ConfirmDialog
      open={!!confirmCancel}
      danger
      title="Cancelar esta sesión"
      message={<>Se cancelará la sesión{confirmCancel?.scheduled_date ? ` del ${fmt(confirmCancel.scheduled_date)}` : ''}, se eliminará la reunión de Teams y se avisará a tu contraparte.</>}
      confirmLabel="Cancelar sesión"
      cancelLabel="Volver"
      onConfirm={doCancel}
      onCancel={() => setConfirmCancel(null)}
    />
    <div className="plan-card">
      <div className="foda-meta">
        <div>
          <h2 style={{ marginBottom: 2 }}>Sesiones del programa</h2>
          <p className="lead" style={{ margin: 0 }}>
            {sessions.length === 0
              ? 'Sin sesiones agendadas todavía'
              : <>{sessions.length} {sessions.length === 1 ? 'sesión agendada' : 'sesiones agendadas'} · <strong>{effective} {effective === 1 ? 'efectiva' : 'efectivas'}</strong></>}
            . {role === 'mentor'
              ? 'Publica tus horarios disponibles; tu mentee agenda cada sesión.'
              : 'Agenda una nueva sesión cuando la necesites.'}
          </p>
        </div>
        {(role === 'mentee' || role === 'mentor') && (
          <button className="btn-ink" onClick={openNewSession}>
            <CalendarPlus size={15} /> Agendar nueva sesión
          </button>
        )}
      </div>

      {sessions.length === 0 && (
        <p className="lead" style={{ color: '#9aa0a8', padding: '10px 2px' }}>
          {role === 'mentee' ? 'Aún no has agendado ninguna sesión. Usa “Agendar nueva sesión”.' : 'Tu mentee aún no ha agendado sesiones.'}
        </p>
      )}

      {/* ---- Sesiones (tarjetas estilo referencia) ---- */}
      <div className="ses-cards">
        {sessions.map(s => {
          const st = STATUS[s.status] || { label: s.status, cls: '' };
          const objs = objsOf(s);
          const d = s.scheduled_date ? new Date(String(s.scheduled_date).replace(' ', 'T')) : null;
          const myRegistered = role === 'mentor' ? (!!s.mentor_feedback || !!s.mentor_rating) : (!!s.mentee_feedback || !!s.session_rating);
          const canRegister = s.status === 'completed' && (role === 'mentee' || role === 'mentor') && !myRegistered;
          const upcoming = s.status === 'scheduled';
          const cardCls = canRegister ? 'is-attention' : s.status === 'completed' ? 'is-completed' : s.status === 'cancelled' ? 'is-cancelled' : '';
          return (
            <div className={`ses-card ${cardCls}`} key={s.id}>
              <div className="ses-card-date">
                {d ? <>
                  <div className="ses-card-day">{d.getDate()}</div>
                  <div className="ses-card-mon">{MESES[d.getMonth()]}</div>
                  <div className="ses-card-time">{d.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}</div>
                </> : <div className="ses-card-mon">S/F</div>}
              </div>
              <div className="ses-card-body">
                <div className="ses-card-tpl">{(s.title || 'Sesión').toUpperCase()}{s.session_type === 'in_person' ? ' · PRESENCIAL' : ''}</div>
                {objs.length > 0 && <div className="ses-card-obj">{objs.length > 1 ? 'Objetivos' : 'Objetivo'}: {objs.join(' · ')}</div>}
                <span className={`session-status ${st.cls}`}>{st.label}{s.duration_minutes ? ` · ${s.duration_minutes} min` : ''}</span>
                {(!!s.session_rating || !!s.mentor_rating) && (
                  <div className="ses-card-ratings">
                    {!!s.session_rating && <span className="ses-rate">Mentee <span className="stars">{'★'.repeat(s.session_rating)}<span className="off">{'★'.repeat(5 - s.session_rating)}</span></span></span>}
                    {!!s.mentor_rating && <span className="ses-rate">Mentor <span className="stars">{'★'.repeat(s.mentor_rating)}<span className="off">{'★'.repeat(5 - s.mentor_rating)}</span></span></span>}
                  </div>
                )}
                {s.mentor_feedback && <div className="ses-card-note"><strong>Mentor:</strong> {s.mentor_feedback}</div>}
                {s.mentee_feedback && <div className="ses-card-note"><strong>Mentee:</strong> {s.mentee_feedback}</div>}
              </div>
              <div className="ses-card-actions">
                {s.meeting_link && s.status !== 'cancelled' && (
                  <a className="btn-teams" href={s.meeting_link} target="_blank" rel="noopener noreferrer"><Video size={13} /> Unirse a la reunión</a>
                )}
                {canRegister && (
                  <button className="btn-ink" onClick={() => openRegister(s)}><Check size={13} /> Registrar</button>
                )}
                {s.status === 'completed' && myRegistered && (
                  <span className="ses-registered"><Check size={13} /> Registrada por ti</span>
                )}
                {upcoming && (role === 'mentee' || role === 'mentor') && (
                  <span className="session-actions">
                    <button className="btn-ghost" onClick={() => openEditSession(s)}><Pencil size={13} /> Editar</button>
                    <button className="btn-ghost" onClick={() => setConfirmCancel(s)}><X size={13} /> Cancelar</button>
                  </span>
                )}
                {s.status === 'cancelled' && (role === 'mentee' || role === 'mentor') && (
                  <button className="btn-ghost" onClick={() => setConfirmDelete(s)}><Trash2 size={13} /> Eliminar</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>

    {/* ---- Panel: agendar una NUEVA sesión (formato referencia) ---- */}
    {scheduleOpen && (
      <div className="feedback-modal-overlay">
        <div className="feedback-modal sf-modal" onClick={e => e.stopPropagation()}>
          <button className="sf-close" onClick={closeSchedule} aria-label="Cerrar"><X size={18} /></button>
          <h3>{editingSession ? `Editar sesión ${editingSession.session_number ?? ''}`.trim() : 'Agendar nueva sesión'}</h3>

          <div className="sf-label">¿Qué van a revisar?</div>
          <div className="sf-focus">
            {[
              { k: 'foda', t: 'Revisión del FODA personal', d: 'Revisar fortalezas, debilidades, oportunidades y amenazas. Priorizar zonas para el PDI.' },
              { k: 'pdi', t: 'Construcción del PDI', d: 'Definir objetivos vinculados al FODA, distribuir acciones 70·20·10 y agendar revisiones.' },
              { k: 'avances', t: 'Revisión de avances', d: 'Repasar acciones cumplidas, recalibrar lo que no funcionó y ajustar próximos pasos.' },
            ].map(o => (
              <button key={o.k} type="button" className={`sf-focus-card ${focus === o.k ? 'on' : ''}`} onClick={() => { setFocus(o.k as any); if (o.k !== 'avances') setObjectives([]); }}>
                <div className="sf-focus-t">{o.t}</div>
                <div className="sf-focus-d">{o.d}</div>
              </button>
            ))}
          </div>

          {focus === 'avances' && (
            <>
              <div className="sf-label">Objetivos del PDI a revisar en esta sesión <span className="sf-hint">selecciona los que apliquen</span></div>
              {comps.length > 0 ? (
                <div className="obj-chips">
                  {comps.map(c => {
                    const on = objectives.includes(Number(c.id));
                    return (
                      <button key={c.id} type="button" className={`obj-chip ${on ? 'on' : ''}`} onClick={() => toggleObjective(Number(c.id))}>
                        {on && <Check size={12} strokeWidth={3} />} {c.title || 'Objetivo'}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="sf-empty">Aún no hay objetivos en el PDI. Construye el PDI primero para poder vincular esta sesión.</div>
              )}
            </>
          )}

          <div className="sf-row">
            <div>
              <div className="sf-label">Fecha y hora</div>
              <input type="datetime-local" className="sf-input" value={freeDate} onChange={e => setFreeDate(e.target.value)} />
            </div>
            <div>
              <div className="sf-label">Duración</div>
              <select className="sf-input" value={duration} onChange={e => setDuration(Number(e.target.value))}>
                <option value={30}>30 min</option><option value={45}>45 min</option><option value={60}>60 min</option><option value={90}>90 min</option>
              </select>
            </div>
          </div>

          <div className="sf-label">Modalidad</div>
          <div className="sf-modality">
            <button type="button" className={`sf-mod-card ${modality === 'teams' ? 'on' : ''}`} onClick={() => setModality('teams')}>
              <div className="sf-mod-t"><Video size={15} /> Microsoft Teams</div>
              <div className="sf-mod-d">Generamos el link al guardar y se sincroniza con el calendario.</div>
            </button>
            <button type="button" className={`sf-mod-card ${modality === 'presencial' ? 'on' : ''}`} onClick={() => setModality('presencial')}>
              <div className="sf-mod-t"><Users size={15} /> Presencial</div>
              <div className="sf-mod-d">La sesión es en persona. Aquí solo la registras (sin link de Teams).</div>
            </button>
          </div>

          <div className="feedback-modal__actions">
            <button className="btn btn--outline btn--small" onClick={closeSchedule}>Cancelar</button>
            <button className="btn btn--primary btn--small" disabled={busy} onClick={confirmSchedule}>{editingSession ? 'Guardar cambios' : 'Agendar sesión'}</button>
          </div>
        </div>
      </div>
    )}

    {/* ---- Feedback / confirmar realización ---- */}
    {fbSession && (
      <div className="feedback-modal-overlay">
        <div className="feedback-modal" onClick={e => e.stopPropagation()}>
          <button className="sf-close" onClick={() => setFbSession(null)} aria-label="Cerrar"><X size={18} /></button>
          <h3>{fbMode === 'outcome' ? `Sesión ${fbSession.session_number} realizada` : 'Mi feedback de la sesión'}</h3>
          <p className="feedback-modal__sub">{fmt(fbSession.scheduled_date)}{fbMode === 'outcome' ? ' · deja tu feedback para marcarla efectiva' : ''}</p>
          <textarea className="feedback-modal__textarea" rows={5}
            placeholder="¿Cómo estuvo la sesión? ¿Qué te llevas? ¿Qué mejorarías?"
            value={fbText} onChange={e => setFbText(e.target.value)} />
          <div className="feedback-modal__actions">
            <button className="btn btn--outline btn--small" onClick={() => setFbSession(null)}>Cancelar</button>
            <button className="btn btn--primary btn--small" disabled={savingFb || !fbText.trim()} onClick={submitFb}>
              {savingFb ? 'Guardando…' : (fbMode === 'outcome' ? 'Marcar efectiva' : 'Guardar feedback')}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* ---- Registrar cómo te fue en la sesión ---- */}
    {regSession && (
      <div className="feedback-modal-overlay">
        <div className="feedback-modal sf-modal" onClick={e => e.stopPropagation()}>
          <button className="sf-close" onClick={closeRegister} aria-label="Cerrar"><X size={18} /></button>
          <h3>¿Cómo te fue en la sesión?</h3>

          {isMentee && (<>
          <div className="sf-label">¿Se cumplió el objetivo de la sesión?</div>
          <div className="reg-yesno">
            <button type="button" className={`reg-yn ${regCompleted === true ? 'on-yes' : ''}`} onClick={() => setRegCompleted(true)}><Check size={14} /> Sí</button>
            <button type="button" className={`reg-yn ${regCompleted === false ? 'on-no' : ''}`} onClick={() => setRegCompleted(false)}><X size={14} /> No</button>
          </div>

          <div className="sf-label">Marca tus avances · acciones completadas</div>
          {actionsForSession(regSession).length > 0 ? (
            <div className="reg-actions">
              {actionsForSession(regSession).map((a: any) => {
                const done = Number(a.completed) === 1;
                const cat = CATS[a.category] || CATS['70'];
                return (
                  <label key={a.id} className={`reg-action ${done ? 'done' : ''}`}>
                    <input type="checkbox" checked={done || regActions.has(a.id)} disabled={done} onChange={() => toggleRegAction(a.id)} />
                    <span className={`pdi-cat cat-${a.category}`}>{cat.label} · {cat.pct}</span>
                    <span className="reg-action-txt">{a.text || 'Acción sin descripción'}</span>
                    {done && <span className="reg-done">ya completada</span>}
                  </label>
                );
              })}
            </div>
          ) : (
            <div className="sf-empty">Este objetivo aún no tiene acciones en el PDI.</div>
          )}
          </>)}

          <div className="sf-label">¿Cómo calificas esta sesión?</div>
          <div className="reg-stars">
            {[1, 2, 3, 4, 5].map(n => (
              <button key={n} type="button" className={`reg-star ${regRating >= n ? 'on' : ''}`} onClick={() => setRegRating(regRating === n ? 0 : n)}>★</button>
            ))}
            <span className="reg-star-lbl">{RATING_LBL[regRating]}</span>
          </div>

          <div className="sf-label">¿Quieres dejarle un mensaje a {role === 'mentee' ? 'tu mentor' : 'tu mentee'}? <span className="sf-hint">opcional</span></div>
          <textarea className="sf-input" rows={3} value={regFeedback} onChange={e => setRegFeedback(e.target.value)}
            placeholder={`Escribe un mensaje a ${role === 'mentee' ? 'tu mentor' : 'tu mentee'} (opcional)`} style={{ resize: 'vertical' }} />

          <div className="sf-label">¿Algo que reportar al equipo del programa? <span className="sf-hint">opcional</span></div>
          <textarea className="sf-input" rows={2} value={regAdminNote} onChange={e => setRegAdminNote(e.target.value)}
            placeholder="Mensaje a los administradores (opcional)" style={{ resize: 'vertical' }} />

          <div className="feedback-modal__actions">
            <button className="btn btn--outline btn--small" onClick={() => { const s = regSession; closeRegister(); setConfirmNo(s); }}>La sesión no se realizó</button>
            <button className="btn btn--primary btn--small" disabled={busy} onClick={submitRegister}>Registrar sesión</button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default Availability;
